// Campeonatos (lote 7, itens 7 e 8): as chamadas à API. A página do campeonato abre sem conta (quem tem o link vê);
// o resto pede a conta Google. A sala de cada jogo nasce na mesa, no primeiro acesso de um dos trios.
import type { Formato, Lado, LinhaDaClassificacao, Transmissao } from '@truco6/campeonatos';
import { ErroDaApi, api } from './api.ts';
import type { Sessao } from './conta.ts';

export type EstadoDoCampeonato = 'inscricao' | 'andamento' | 'concluido' | 'cancelado';
export type Sinais = 'desligados' | 'aviso' | 'todos';
export interface Pessoa { usuario: string; apelido: string; avatar: string | null }
export interface TrioNaTela { id: string; nome: string; escudo: string; estado: 'pendente' | 'aprovada' | 'recusada'; capitao: string; membros: Pessoa[] }
export interface JogoNaTela {
  id: string; fase: 'liga' | 'grupo' | 'mata'; rodada: number; nomeDaRodada: string; grupo: number | null; posicao: number;
  a: string | null; b: string | null; // o id do trio; '' é passe livre; null, a definir
  melhorDe: 1 | 3; partidas: { vencedor: Lado; pontos: [number, number] }[]; vencedor: Lado | null; wo: Lado | 'ambos' | null; manual: boolean;
  inicio: number; fim: number; marcadoPara: number | null; prazo: number; motivo: string | null;
  sala: string | null; meu: boolean; transmitido: boolean;
}
export interface ResumoCampeonato {
  id: string; slug: string; nome: string; formato: Formato; estado: EstadoDoCampeonato; trios: number; inscricaoAte: number;
  comunidade: string | null; campeao: string | null; organizador: boolean;
}
export interface FichaCampeonato {
  id: string; slug: string; nome: string; formato: Formato; nomeDoFormato: string; returno: boolean; melhorDe: 1 | 3;
  regras: { tempoPorJogadaMs: number; sinais: Sinais }; premio: string | null; avisoDoPremio: string | null;
  inscricaoAte: number; aprovacao: boolean; publico: boolean; transmissao: Transmissao; diasPorRodada: number; estado: EstadoDoCampeonato;
  campeao: string | null; criadoEm: number; iniciadoEm: number | null; concluidoEm: number | null; canceladoEm: number | null; motivoCancelamento: string | null;
  organizador: Pessoa; comunidade: { id: string; nome: string; escudo: string } | null;
  trios: TrioNaTela[]; jogos: JogoNaTela[]; classificacao: { grupo: number | null; linhas: LinhaDaClassificacao[] }[];
  eu: { organizador: boolean; trio: string | null; podeInscrever: { id: string; nome: string; escudo: string }[]; ferramentas: boolean } | null;
}
export interface MeusCampeonatos { organizados: ResumoCampeonato[]; inscritos: ResumoCampeonato[]; podeCriar: boolean; ativos: number; limite: number }
export interface NovoCampeonato {
  nome: string; formato: Formato; returno: boolean; melhorDe: 1 | 3; regras: { tempoPorJogadaMs: number; sinais: Sinais };
  premio: string; inscricaoAte: number; aprovacao: boolean; diasPorRodada: number; comunidade: string | null;
}

const k = (id: string) => `/campeonatos/${id}`;
export const linkDoCampeonato = (slug: string) => `${location.origin}/campeonato/${slug}`;

export const meusCampeonatos = (s: Sessao) => api<MeusCampeonatos>(s, 'GET', '/campeonatos');
export const campeonatosDaComunidade = (s: Sessao, comunidade: string) =>
  api<{ campeonatos: ResumoCampeonato[]; podeCriar: boolean }>(s, 'GET', `/campeonatos/comunidade/${comunidade}`);
export const criarCampeonato = (s: Sessao, b: NovoCampeonato) => api<FichaCampeonato>(s, 'POST', '/campeonatos', b);
export const editarCampeonato = (s: Sessao, id: string, b: Record<string, unknown>) => api<FichaCampeonato>(s, 'POST', k(id) + '/editar', b);
export const inscreverTrio = (s: Sessao, id: string, trio: string) => api<FichaCampeonato>(s, 'POST', k(id) + '/inscrever', { trio });
export const decidirInscricao = (s: Sessao, id: string, trio: string, acao: 'aprovar' | 'recusar' | 'tirar') =>
  api<FichaCampeonato>(s, 'POST', `${k(id)}/inscricoes/${trio}/${acao}`);
export const comecarCampeonato = (s: Sessao, id: string, inicio: number | null) => api<FichaCampeonato>(s, 'POST', k(id) + '/comecar', inicio ? { inicio } : {});
export const cancelarCampeonato = (s: Sessao, id: string, motivo: string) => api<{ ok: true }>(s, 'POST', k(id) + '/cancelar', { motivo });
export const mudarJanela = (s: Sessao, id: string, rodada: number, inicio: number, fim: number) => api<FichaCampeonato>(s, 'POST', `${k(id)}/rodadas/${rodada}`, { inicio, fim });
export const marcarJogo = (s: Sessao, id: string, jogo: string, quando: number | null, motivo: string) =>
  api<FichaCampeonato>(s, 'POST', `${k(id)}/jogos/${jogo}/marcar`, { quando, motivo });
export const lancarResultado = (s: Sessao, id: string, jogo: string, b: { vencedor?: Lado; wo?: Lado | 'ambos'; motivo: string }) =>
  api<FichaCampeonato>(s, 'POST', `${k(id)}/jogos/${jogo}/resultado`, b);
// Ferramentas de organizador (Ouro).
export const convidarParaCampeonato = (s: Sessao, id: string, usuarios: string[]) => api<{ enviados: number }>(s, 'POST', k(id) + '/convidar', { usuarios });
export const avisarInscritos = (s: Sessao, id: string, texto: string) => api<{ enviados: number }>(s, 'POST', k(id) + '/aviso', { texto });
export const exportarCampeonato = (s: Sessao, id: string) => api<{ arquivo: string; csv: string }>(s, 'GET', k(id) + '/csv');

// Baixa o CSV no aparelho (com o BOM, para a planilha abrir os acentos certos).
export function baixarCsv(arquivo: string, csv: string): void {
  const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = arquivo;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// A página: com a conta, mostra o que é seu (sua inscrição, a sala do seu jogo); sem conta, só o que é aberto.
export async function lerCampeonato(s: Sessao | null, slug: string): Promise<FichaCampeonato> {
  if (s) return api<FichaCampeonato>(s, 'GET', `/campeonatos/pagina/${slug}`);
  const r = await fetch(`/api/campeonatos/pagina/${slug}`).catch(() => null);
  if (!r) throw new ErroDaApi(0, 'sem_conexao', 'Sem conexão com o servidor. Tente de novo.');
  const d = (await r.json().catch(() => null)) as (FichaCampeonato & { erro?: { codigo: string; mensagem: string } }) | null;
  if (r.ok && d) return d;
  throw new ErroDaApi(r.status, d?.erro?.codigo ?? 'erro', d?.erro?.mensagem ?? 'Não deu para abrir o campeonato.');
}

const FUSO = 'America/Sao_Paulo';
// "ter., 10 de nov. · 20h", no horário de Brasília.
export function quandoDoJogo(ms: number): string {
  const d = new Date(ms);
  const dia = d.toLocaleDateString('pt-BR', { timeZone: FUSO, weekday: 'short', day: 'numeric', month: 'short' });
  const [h, m] = d.toLocaleTimeString('pt-BR', { timeZone: FUSO, hour: '2-digit', minute: '2-digit', hour12: false }).split(':');
  return `${dia} · ${Number(h)}h${m === '00' ? '' : m}`;
}
export const diaDoJogo = (ms: number) => new Date(ms).toLocaleDateString('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit' });

// O nome do trio de cada lado: passe livre ou a definir.
export function nomeDoLado(f: FichaCampeonato, t: string | null): string {
  if (t === null) return 'A definir';
  if (t === '') return 'Passe livre';
  return f.trios.find((x) => x.id === t)?.nome ?? 'Trio';
}
